import React, { PropsWithChildren, useState, useCallback, useEffect } from 'react'
import Button from '../Button'
import Modal from '../Modal'
import useMonitor from '../utils/useMonitor'
import { IIdleMonitor } from './interfaces'
import useResetRestAtoms from '../SessionExpireWrapper/utils/useResetAtoms'
import useSessionExpiry from '../SessionExpireWrapper/hooks/useSessionExpiry'

type Messaging = {
  header?: string
  subheader?: string
}

const sharedMessaging: Messaging = {
  header: 'Page Timeout',
  subheader: 'Due to prolonged inactivity, you have been redirected to the home page'
}

const sessionExpiryMessaging: Messaging = {
  header: 'Your session is about to expire!',
  subheader: 'You have unsaved MattressMatcher® changes. Do you wish to save your changes?'
}

const IdleMonitorWrapper = ({
  timeout,
  sessionExpiry,
  isEditingQuiz,
  onTimeOut,
  children,
  onModalClose
}: PropsWithChildren<IIdleMonitor>) => {
  const [showModal, setShowModal] = useState(false)
  const [disableTimeout, setDisableTimeout] = useState(false)
  const [messaging, setMessaging] = useState<Messaging>({})
  const [sessionExpireMessaging, setIsSessionExpireMessaging] = useState<Messaging>({})
  const [isSessionExpiryModal, setIsSessionExpiryModal] = useState(false)
  const { resetAtoms } = useResetRestAtoms()

  const isSessionExpiry = useSessionExpiry(sessionExpiry, isEditingQuiz)

  const updateMessaging = useCallback(() => {
    setMessaging(sharedMessaging)
  }, [])

  const updateSessionExpiryMessaging = useCallback(() => {
    setIsSessionExpireMessaging(sessionExpiryMessaging)
  }, [])

  const handleSessionExpiry = useCallback(() => {
    if (disableTimeout) return
    updateSessionExpiryMessaging()
    setIsSessionExpiryModal(true)
    setShowModal(true)
  }, [disableTimeout])

  useEffect(() => {
    isSessionExpiry ? updateSessionExpiryMessaging() : updateMessaging()
  }, [isSessionExpiry])

  const handleSessionExpiryClose = useCallback(() => {
    if (isSessionExpiryModal) {
      setShowModal(false)
      setIsSessionExpiryModal(false)
    }
  }, [isSessionExpiryModal])

  const handleTimeout = useCallback(() => {
    if (disableTimeout) return

    setDisableTimeout(true)
    updateMessaging()
    setIsSessionExpiryModal(false)
    setShowModal(true)
    resetAtoms()
    onTimeOut?.()
  }, [onTimeOut, isSessionExpiry, disableTimeout, updateMessaging, resetAtoms])

  const handleClose = useCallback(() => {
    setShowModal(false)
    setDisableTimeout(false)
    onModalClose?.()
  }, [onModalClose])

  const handleEditQuiz = useCallback(() => {
    if (window.location.pathname.includes('/mattressmatcher')) {
      handleClose()
    } else {
      setShowModal(false)
      setDisableTimeout(true)
      window.location.href = '/mattressmatcher'
    }
  }, [handleClose])

  useMonitor({
    timeout,
    sessionExpiry,
    isEditingQuiz,
    onTimeOut: handleTimeout,
    onSessionExpiry: handleSessionExpiry,
    onSessionExpiryClose: handleSessionExpiryClose
  })

  return (
    <>
      {children}
      {showModal &&
        (isSessionExpiry ? (
          <Modal
            dreamhub
            isOpen
            header={sessionExpireMessaging.header}
            subheader={sessionExpireMessaging.subheader}
            zIndex={1002}
            timeout={timeout}
            sessionExpiry={sessionExpiry}
            isEditingQuiz={isEditingQuiz}
          >
            <>
              <Button onClick={handleEditQuiz} size="md" btnType="primary-blue">
                Yes, take me there now!
              </Button>
              <Button onClick={handleClose} size="md" btnType="tertiary">
                No Thanks
              </Button>
            </>
          </Modal>
        ) : (
          <Modal dreamhub isOpen header={messaging.header} subheader={messaging.subheader} zIndex={1002}>
            <>
              <Button onClick={handleClose} size="md" btnType="primary-blue">
                Close
              </Button>
            </>
          </Modal>
        ))}
    </>
  )
}

export default IdleMonitorWrapper
